import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useProject } from '../hooks/useProject'
import { useTasks } from '../hooks/useTasks'
import { useCurvaS } from '../hooks/useCurvaS'
import { LoadingSkeleton } from '../components/common/LoadingSkeleton'
import { SCurveChart } from '../components/charts/SCurveChart'

interface KPICardProps {
  title: string
  value: string
  subtitle: string
  trend: 'up' | 'down' | 'neutral'
  color: 'blue' | 'green' | 'yellow' | 'red'
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, trend, color }) => {
  const colorClasses = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    yellow: 'border-yellow-200 bg-yellow-50',
    red: 'border-red-200 bg-red-50'
  }

  const trendIcon = {
    up: '↑',
    down: '↓',
    neutral: '→'
  }

  return (
    <div className={`rounded-lg border p-6 shadow-sm ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <div className="text-2xl">{trendIcon[trend]}</div>
      </div>
    </div>
  )
}

const DashboardPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const numericProjectId = projectId ? parseInt(projectId) : 0

  const { data: project, isLoading: isLoadingProject } = useProject(numericProjectId)
  const { data: tasksData, isLoading: isLoadingTasks } = useTasks(numericProjectId)
  const { data: curvaData, isLoading: isLoadingCurva } = useCurvaS(numericProjectId)

  if (isLoadingProject || isLoadingTasks || isLoadingCurva) {
    return <LoadingSkeleton />
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <p className="text-gray-500">Project not found</p>
        </div>
      </div>
    )
  }

  // Calculate KPIs
  const totalTasks = tasksData?.tasks.length || 0
  const completedTasks = tasksData?.tasks.filter(task => task.status === 'completed').length || 0
  const inProgressTasks = tasksData?.tasks.filter(task => task.status === 'in_progress').length || 0
  const delayedTasks = tasksData?.tasks.filter(task => task.status === 'delayed').length || 0

  const completionPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0

  // Calculate schedule performance
  const onTimeTasks = tasksData?.tasks.filter(task => {
    if (!task.endDate) return true
    const endDate = new Date(task.endDate)
    const now = new Date()
    return task.status === 'completed' || endDate >= now
  }).length || 0

  const schedulePerformance = totalTasks > 0 ? Math.round((onTimeTasks / totalTasks) * 100) : 0

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
            <p className="text-gray-600 mt-1">Project Dashboard & Overview</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Project ID: {project.id}</p>
            <p className="text-sm text-gray-500">Status: {project.status || 'Active'}</p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          title="Task Completion"
          value={`${completionPercentage}%`}
          subtitle={`${completedTasks} of ${totalTasks} tasks completed`}
          trend={completionPercentage > 50 ? 'up' : 'down'}
          color="blue"
        />
        <KPICard
          title="Schedule Performance"
          value={`${schedulePerformance}%`}
          subtitle="Tasks on schedule"
          trend={schedulePerformance > 80 ? 'up' : 'down'}
          color="green"
        />
        <KPICard
          title="Active Tasks"
          value={inProgressTasks.toString()}
          subtitle="Currently in progress"
          trend="neutral"
          color="yellow"
        />
        <KPICard
          title="Delayed Tasks"
          value={delayedTasks.toString()}
          subtitle="Require attention"
          trend={delayedTasks > 0 ? 'down' : 'up'}
          color="red"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* S-Curve Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Project Progress (S-Curve)</h2>
          <div className="h-64">
            {curvaData?.aggregates ? (
              <SCurveChart data={curvaData.aggregates} />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No S-curve data available
              </div>
            )}
          </div>
        </div>

        {/* Task Status Overview */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Task Status Overview</h2>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Planned</span>
              <span className="text-sm font-medium">{tasksData?.tasks.filter(t => t.status === 'planned').length || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">In Progress</span>
              <span className="text-sm font-medium">{inProgressTasks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Completed</span>
              <span className="text-sm font-medium">{completedTasks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Delayed</span>
              <span className="text-sm font-medium">{delayedTasks}</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Overall Progress</span>
              <span>{completionPercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate(`/projects/${projectId}/boq`)}
            className="btn-secondary text-center"
          >
            View BoQ
          </button>
          <button
            onClick={() => navigate(`/projects/${projectId}/schedule`)}
            className="btn-secondary text-center"
          >
            View Schedule
          </button>
          <button
            onClick={() => navigate(`/projects/${projectId}/cost`)}
            className="btn-secondary text-center"
          >
            Cost Control
          </button>
          <button
            onClick={() => navigate(`/projects/${projectId}/tasks`)}
            className="btn-secondary text-center"
          >
            Manage Tasks
          </button>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage